/**
 * AP-24: Einen Tag des Monatsberichts bearbeiten — alle Felder der Zeile.
 *
 * Anwesenheit und Pause (nur im händischen Modus), Fehlzeit samt Art, und die
 * Diäten-Felder des Mandanten. Die Nettozeit steht nicht als Feld da: sie ist
 * Anwesenheit − Pause und wird live mitgerechnet.
 *
 * **Die Pause zieht ab.** Genau wie in der Stempelmaske (`duration = Ende −
 * Start − Pause`) — zwei Denkmodelle für dasselbe Wort wären eine Falle.
 * Gespeichert wird weiterhin die Nettozeit (`workMinutes`); die Umrechnung
 * passiert hier, damit sich an der Bedeutung der Daten nichts ändert.
 *
 * Gegenstück zum `DayEditorSheet` der PWA. Beide holen ihren Inhalt aus
 * demselben Endpunkt (`GET/PUT /monthly-report/day`), damit sie beim nächsten
 * Feld nicht auseinanderlaufen.
 */

import { useCallback, useEffect, useState } from 'react'
import { Modal } from '@symphenia/ui-components'
import { fmtDate } from '@/lib/format-date'
import { apiErrorText } from '@/lib/api-error'
import { formatHours } from '@/lib/hours-format'
import {
  monthlyReportApi,
  type ReportDay,
  type ReportDayFields,
  type SaveReportDayPayload,
} from '../api/monthly-report.api'

interface Props {
  date: string
  onClose: () => void
  onSaved: () => void
}

/**
 * Nimmt „8,5" / „8.5" und „8:30" gleichermaßen an — unabhängig vom
 * eingestellten Anzeigeformat. Leer = nicht erfasst (`null`);
 * `undefined` = Eingabe unverständlich.
 */
export function parseHoursInput(raw: string): number | null | undefined {
  const v = raw.trim()
  if (v === '') return null
  if (v.includes(':')) {
    const [h, m] = v.split(':')
    const hh = Number(h)
    const mm = Number(m)
    if (!Number.isFinite(hh) || !Number.isFinite(mm) || mm < 0 || mm > 59) return undefined
    return Math.round(hh * 60 + mm)
  }
  const num = Number(v.replace(',', '.'))
  if (!Number.isFinite(num) || num < 0) return undefined
  return Math.round(num * 60)
}

/**
 * AP-26 — die zehn getypten Tagesfelder als Formularwerte.
 *
 * Alles als String, Ja/Nein als `'true'`/`'false'` — dieselbe Konvention wie bei
 * den Diäten-Feldern weiter unten. Welche Felder überhaupt vorkommen, sagt der
 * Server (`dayFields.visible`); hier steht keine zweite Liste.
 */
function dayFieldsToForm(f: ReportDayFields): Record<string, string> {
  const v = f.values
  return {
    startTime: v.startTime,
    endTime: v.endTime,
    departure: v.departure,
    returnTime: v.returnTime,
    travelMinutes: v.travelMinutes != null ? String(v.travelMinutes) : '',
    appointmentMinutes: v.appointmentMinutes != null ? String(v.appointmentMinutes) : '',
    km: v.km != null ? String(v.km) : '',
    appointment: v.appointment,
    isDriver: String(v.isDriver),
    overnight: String(v.overnight),
  }
}

/**
 * AP-26-Nachtrag — Spanne zwischen zwei `HH:MM`-Eingaben in Minuten.
 *
 * Gegenstück zu `spanMinutes` im Backend, und aus demselben Grund: Ein Ende,
 * das nicht nach dem Beginn liegt, gilt als am Folgetag (Nachtschicht).
 * Gerechnet wird hier nur zur **Anzeige** — gespeichert wird, was der Server
 * daraus ableitet.
 */
export function spanMinutes(von: string, bis: string): number | null {
  const teil = (v: string) => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(v.trim())
    return m ? Number(m[1]) * 60 + Number(m[2]) : null
  }
  const a = teil(von)
  const b = teil(bis)
  if (a === null || b === null) return null
  const roh = b - a
  return roh > 0 ? roh : roh + 24 * 60
}

/** Ganze Zahl aus einer Eingabe. Leer = nicht erfasst, `undefined` = unlesbar. */
function parseIntInput(raw: string): number | null | undefined {
  const v = raw.trim()
  if (v === '') return null
  const n = Number(v.replace(',', '.'))
  if (!Number.isFinite(n) || n < 0) return undefined
  return Math.round(n)
}

const fieldCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50'
const labelCls = 'mb-1 block text-xs font-medium text-muted-foreground'

export function ReportDayDialog({ date, onClose, onSaved }: Props) {
  const [day, setDay] = useState<ReportDay | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [brutto, setBrutto] = useState('')
  const [pause, setPause] = useState('')
  const [absType, setAbsType] = useState('')
  const [absHours, setAbsHours] = useState('')
  const [lv, setLv] = useState<Record<string, string>>({})
  const [df, setDf] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await monthlyReportApi.getDay(date)
      setDay(data)
      // null bleibt leeres Feld: „nicht erfasst" ist etwas anderes als „0".
      // Gespeichert ist die Nettozeit — im Feld steht die Anwesenheit.
      setBrutto(
        data.workMinutes != null
          ? formatHours(data.workMinutes + (data.breakMinutes ?? 0), data.hoursFormat)
          : '',
      )
      setPause(data.breakMinutes != null ? formatHours(data.breakMinutes, data.hoursFormat) : '')
      setAbsType(data.absence?.absenceTypeId ?? '')
      setAbsHours(data.absence ? formatHours(data.absence.durationMinutes, data.hoursFormat) : '')
      setLv(
        Object.fromEntries(
          data.lvSchema.map((f) => [
            f.name,
            data.lvValues[f.name] != null ? String(data.lvValues[f.name]) : '',
          ]),
        ),
      )
      setDf(dayFieldsToForm(data.dayFields))
    } catch (err) {
      setError(apiErrorText(err, 'Tag konnte nicht geladen werden.'))
    } finally {
      setLoading(false)
    }
  }, [date])

  useEffect(() => { void load() }, [load])

  const save = async () => {
    if (!day) return
    const payload: SaveReportDayPayload = { date }

    if (day.hoursEditable && zeitenAlsQuelle) {
      // Kommt und Geht sind hier Pflicht: Die Bruttozeit gibt es sonst nicht.
      // `workMinutes` wandert NICHT in den Payload — der Server leitet es ab,
      // damit die Formel nicht in vier Masken einzeln steht.
      if (spanne === null) {
        setError('Bitte Kommt und Geht angeben.')
        return
      }
      const p = parseHoursInput(pause)
      if (p === undefined) {
        setError('Bitte die Pause als 0,5 oder 0:30 eingeben.')
        return
      }
      if ((p ?? 0) > spanne) {
        setError('Die Pause ist länger als die Anwesenheit.')
        return
      }
      payload.breakMinutes = p ?? 0
    } else if (day.hoursEditable) {
      const anwesend = parseHoursInput(brutto)
      const p = parseHoursInput(pause)
      if (anwesend === undefined || p === undefined) {
        setError('Bitte Stunden als 8,5 oder 8:30 eingeben.')
        return
      }
      if (anwesend === null) {
        // Keine Anwesenheit erfasst → der ganze Tag fällt auf den Stempelwert
        // zurück. Eine Pause ohne Anwesenheit wäre eine Angabe über nichts.
        payload.workMinutes = null
        payload.breakMinutes = null
      } else {
        const pausenMin = p ?? 0
        if (anwesend > 1440) {
          setError('Die Anwesenheit kann nicht mehr als 24 Stunden betragen.')
          return
        }
        if (pausenMin > anwesend) {
          setError('Die Pause ist länger als die Anwesenheit.')
          return
        }
        payload.workMinutes = anwesend - pausenMin
        payload.breakMinutes = pausenMin
      }
    }

    if (day.absenceEditable) {
      const h = parseHoursInput(absHours)
      if (h === undefined) {
        setError('Bitte die Fehlzeit als 8 oder 8:00 eingeben.')
        return
      }
      if (absType && h != null && h > 0) {
        payload.absence = { absenceTypeId: absType, durationMinutes: h }
      } else if (day.absence) {
        // Art abgewählt oder Stunden geleert → Fehlzeit des Tages entfernen.
        payload.absence = null
      }
    }

    // ── AP-26: nur die sichtbaren Felder, und nur die änderbaren ────────────
    // Was der Admin nicht eingeschaltet hat, steht nicht im Payload — und wird
    // damit auch nicht überschrieben (feldweises Schreiben).
    for (const f of day.dayFields.visible) {
      const raw = df[f.key] ?? ''
      if (f.kind === 'time') {
        if (!day.dayFields.timesEditable && (f.key === 'startTime' || f.key === 'endTime')) continue
        payload[f.key as 'startTime'] = raw === '' ? null : raw
      } else if (f.kind === 'boolean') {
        payload[f.key as 'isDriver'] = raw === 'true'
      } else if (f.kind === 'number') {
        const n = parseIntInput(raw)
        if (n === undefined) {
          setError(`Bitte ${f.label} als ganze Zahl eingeben.`)
          return
        }
        payload[f.key as 'km'] = n
      } else if (f.kind === 'hours') {
        const m = parseHoursInput(raw)
        if (m === undefined) {
          setError(`Bitte ${f.label} als 1,5 oder 1:30 eingeben.`)
          return
        }
        payload[f.key as 'travelMinutes'] = m
      } else {
        payload[f.key as 'appointment'] = raw === '' ? null : raw
      }
    }

    if (day.lvSchema.length > 0) {
      payload.customFields = Object.fromEntries(
        day.lvSchema.map((f) => [
          f.name,
          // Checkbox als echten Boolean ablegen — sonst stünde in der Datenbank
          // mal `true` und mal `"true"`, je nachdem welche Maske gespeichert hat.
          f.type === 'checkbox' ? lv[f.name] === 'true' : (lv[f.name] ?? ''),
        ]),
      )
    }

    setSaving(true)
    setError(null)
    try {
      await monthlyReportApi.saveDay(payload)
      onSaved()
    } catch (err) {
      setError(apiErrorText(err, 'Speichern fehlgeschlagen.'))
    } finally {
      setSaving(false)
    }
  }

  /**
   * AP-26-Nachtrag — sind Kommt UND Geht eingeschaltet und änderbar, sind sie
   * die Eingabe und die Bruttozeit fällt daraus ab. Der Mandant, der sie nicht
   * eingeschaltet hat, tippt weiter die Anwesenheit ein.
   */
  const zeitenAlsQuelle =
    (day?.dayFields.timesEditable ?? false) &&
    ['startTime', 'endTime'].every((k) => day?.dayFields.visible.some((f) => f.key === k))

  const spanne = zeitenAlsQuelle ? spanMinutes(df.startTime ?? '', df.endTime ?? '') : null

  const hint = day?.stamped
    ? `Gestempelt: ${formatHours(day.stamped.netMinutes + day.stamped.breakMinutes, day.hoursFormat)} Anwesenheit, ${formatHours(day.stamped.breakMinutes, day.hoursFormat)} Pause.`
    : null

  /**
   * Die Rechnung live mitschreiben. Ohne sie bliebe die Wirkung einer Eingabe
   * unsichtbar, sobald die Spalten Bruttozeit und Pause in der Liste
   * ausgeblendet sind — genau daran ist die Pause zuerst unbemerkt geblieben.
   */
  const rechnung = (() => {
    if (!day?.hoursEditable) return null
    const f = (m: number) => formatHours(m, day.hoursFormat)
    if (zeitenAlsQuelle) {
      if (spanne === null) return 'Kommt und Geht angeben — daraus ergibt sich die Bruttozeit.'
      const p = parseHoursInput(pause)
      if (p === undefined) return 'Eingabe nicht lesbar.'
      const pausenMin = p ?? 0
      if (pausenMin > spanne) return 'Die Pause ist länger als die Anwesenheit.'
      return `Brutto ${f(spanne)} · Pause ${f(pausenMin)} · Netto ${f(spanne - pausenMin)}`
    }
    const anwesend = parseHoursInput(brutto)
    const p = parseHoursInput(pause)
    if (anwesend === undefined || p === undefined) return 'Eingabe nicht lesbar.'
    if (anwesend === null) return 'Nicht erfasst — es zählt die gestempelte Zeit.'
    const pausenMin = p ?? 0
    if (pausenMin > anwesend) return 'Die Pause ist länger als die Anwesenheit.'
    return `Brutto ${f(anwesend)} · Pause ${f(pausenMin)} · Netto ${f(anwesend - pausenMin)}`
  })()

  return (
    <Modal isOpen onClose={() => !saving && onClose()} variant="center" size="sm" padded={false} hideCloseButton>
      <div className="space-y-4 p-6">
        <div>
          <h2 className="text-base font-semibold text-foreground">Tag bearbeiten</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {fmtDate(date)}{day ? ` · ${day.weekday}` : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : day ? (
          <div className="space-y-4">
            {day.locked && (
              <p className="rounded-lg bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                Der Monat ist verbindlich abgegeben und damit gesperrt.
              </p>
            )}

            {day.hoursEditable && (
              <div>
                <div className="grid grid-cols-2 gap-3">
                  {/* Sind Kommt und Geht die Quelle, gibt es die Anwesenheit nicht
                      mehr als Eingabe — sie stünde sonst neben ihrer eigenen
                      Herleitung und könnte ihr widersprechen. */}
                  {!zeitenAlsQuelle && (
                    <div>
                      <label className={labelCls} htmlFor="rd-work">Anwesenheit (brutto)</label>
                      <input id="rd-work" type="text" inputMode="decimal" value={brutto} placeholder="–"
                        disabled={saving} onChange={(e) => setBrutto(e.target.value)} className={fieldCls} />
                    </div>
                  )}
                  <div>
                    <label className={labelCls} htmlFor="rd-break">Pause</label>
                    <input id="rd-break" type="text" inputMode="decimal" value={pause} placeholder="–"
                      disabled={saving} onChange={(e) => setPause(e.target.value)} className={fieldCls} />
                  </div>
                </div>
                {rechnung && (
                  <p className="mt-2 rounded-lg bg-muted/40 px-3 py-2 text-sm font-medium text-foreground">
                    {rechnung}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {zeitenAlsQuelle
                    ? 'Die Bruttozeit ergibt sich aus Kommt und Geht; die Pause wird davon abgezogen. Ein Ende vor dem Beginn gilt als Folgetag.'
                    : 'Die Pause wird von der Anwesenheit abgezogen. 8,5 oder 8:30. Leer heißt „nicht erfasst" — dann zählt die gestempelte Zeit.'}
                  {hint ? ` ${hint}` : ''}
                </p>
              </div>
            )}

            {day.absenceEditable && day.absenceTypes.length > 0 && (
              <div className="grid grid-cols-2 gap-3 border-t border-border pt-4">
                <div>
                  <label className={labelCls} htmlFor="rd-abs-type">Fehlzeit-Art</label>
                  <select id="rd-abs-type" value={absType} disabled={saving}
                    onChange={(e) => setAbsType(e.target.value)} className={fieldCls}>
                    <option value="">– keine –</option>
                    {day.absenceTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="rd-abs-hours">Fehlzeit</label>
                  <input id="rd-abs-hours" type="text" inputMode="decimal" value={absHours} placeholder="–"
                    disabled={saving} onChange={(e) => setAbsHours(e.target.value)} className={fieldCls} />
                </div>
              </div>
            )}
            {day.absenceCount > 1 && (
              <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                An diesem Tag sind {day.absenceCount} Fehlzeiten erfasst. Sie lassen sich hier nicht
                zusammenfassen — Änderungen bitte in der Fehlzeiten-Erfassung.
              </p>
            )}

            {day.dayFields.visible.length > 0 && (
              <div className="grid grid-cols-2 gap-3 border-t border-border pt-4">
                {day.dayFields.visible.map((f) => {
                  const id = `rd-df-${f.key}`
                  // Kommt/Geht folgen derselben Regel wie die Stunden: im
                  // Zeiterfassungs-Modus kommen sie aus den Stempelungen.
                  const zeitGesperrt =
                    (f.key === 'startTime' || f.key === 'endTime') && !day.dayFields.timesEditable
                  const disabled = saving || day.locked || zeitGesperrt
                  return (
                    <div key={f.key} className={f.kind === 'text' ? 'col-span-2' : undefined}>
                      <label className={labelCls} htmlFor={id}>
                        {f.label}
                        {zeitenAlsQuelle && (f.key === 'startTime' || f.key === 'endTime') && (
                          <span className="text-destructive"> *</span>
                        )}
                      </label>
                      {f.kind === 'boolean' ? (
                        <input
                          id={id}
                          type="checkbox"
                          checked={df[f.key] === 'true'}
                          disabled={disabled}
                          onChange={(e) => setDf((p) => ({ ...p, [f.key]: String(e.target.checked) }))}
                          className="h-4 w-4 accent-[hsl(var(--primary))]"
                        />
                      ) : (
                        <input
                          id={id}
                          // `type="time"` gibt dem Browser die Uhrzeit-Tastatur und
                          // liefert immer HH:MM — genau das Format, das der Server
                          // erwartet.
                          type={f.kind === 'time' ? 'time' : 'text'}
                          inputMode={f.kind === 'number' || f.kind === 'hours' ? 'decimal' : 'text'}
                          value={df[f.key] ?? ''}
                          placeholder="–"
                          disabled={disabled}
                          onChange={(e) => setDf((p) => ({ ...p, [f.key]: e.target.value }))}
                          className={fieldCls}
                        />
                      )}
                    </div>
                  )
                })}
                {!day.dayFields.timesEditable &&
                  day.dayFields.visible.some((f) => f.key === 'startTime' || f.key === 'endTime') && (
                    <p className="col-span-2 text-xs text-muted-foreground">
                      Kommt und Geht stammen aus den Stempelungen und sind hier nicht änderbar.
                    </p>
                  )}
              </div>
            )}

            {day.lvSchema.length > 0 && (
              <div className="space-y-3 border-t border-border pt-4">
                {day.lvSchema.map((f) => (
                  <div key={f.name}>
                    {/* Der Zusatz „Diäten" trennt ein frei benanntes Typ-B-Feld von
                        einer gleichnamigen getypten Spalte — ein Mandant mit einem
                        Diäten-Feld „Kilometer" sieht sonst zweimal dasselbe Wort. */}
                    <label className={labelCls} htmlFor={`rd-lv-${f.name}`}>
                      {f.label || f.name}
                      <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Diäten</span>
                    </label>
                    {f.type === 'checkbox' ? (
                      <input
                        id={`rd-lv-${f.name}`}
                        type="checkbox"
                        checked={lv[f.name] === 'true'}
                        disabled={saving || day.locked}
                        onChange={(e) => setLv((p) => ({ ...p, [f.name]: String(e.target.checked) }))}
                        className="h-4 w-4 accent-[hsl(var(--primary))]"
                      />
                    ) : (
                      <input
                        id={`rd-lv-${f.name}`}
                        type="text"
                        inputMode={f.type === 'number' || f.type === 'currency' ? 'decimal' : 'text'}
                        value={lv[f.name] ?? ''}
                        placeholder="–"
                        disabled={saving || day.locked}
                        onChange={(e) => setLv((p) => ({ ...p, [f.name]: e.target.value }))}
                        className={fieldCls}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex gap-3">
          <button onClick={onClose} disabled={saving}
            className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted/80 disabled:opacity-40">
            {day?.locked ? 'Schließen' : 'Abbrechen'}
          </button>
          {!day?.locked && (
            <button onClick={save} disabled={saving || loading}
              className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
              {saving ? 'Wird gespeichert…' : 'Speichern'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
